import "./Personalize.scss";
import React, { useEffect, useRef, useState } from "react";
import {
  IonButton,
  IonContent,
  IonIcon,
  IonImg,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonPage,
  IonSlide,
  IonSlides,
  isPlatform,
} from "@ionic/react";
import { Plugins } from "@capacitor/core";
import isValidUTF8 from "utf-8-validate";
import abaxxLogo from "../../assets/svg/logoAbaxx.svg";
import { useHistory } from "react-router-dom";
import { useTracked } from "../../state";

import phoneLockImage from "../../assets/svg/phone-lock.svg";
import clsx from "clsx";
import { getUserByIdentity, updateUser } from "../../api/user";
import {
  ShowToastErrorMessage,
  ShowToastSuccessMessage,
} from "../../utils/helpers/ToastMessage";
import briefcaseIcon from "../../assets/svg/accountIcons/briefcase.svg";
import LabelIconDropdown from "../../components/LabelIconDropdown";
import { AuthMode } from "@ionic-enterprise/identity-vault";

const { Keyboard } = Plugins;

const slideOpts = {
  initialSlide: 0,
  speed: 400,
};

const Personalize: React.FC = () => {
  const history = useHistory();
  const slidesRef = useRef<any>(null);
  const [state, dispatch] = useTracked();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpenKeyboard, setIsOpenKeyboard] = useState<boolean>(false);

  const [accountLabel, setAccountLabel] = useState<any>("");
  const [accountEmail, setAccountEmail] = useState<any>(state.user.email);
  const [selectedIcon, setSelectedIcon] = useState<any>(briefcaseIcon);
  const [selectedColor, setSelectedColor] = useState<any>("#53A6DE");
  const [selectedIconName, setSelectedIconName] = useState<any>("briefcase");

  const [accountLabelError, setAccountLabelError] = useState("");
  const [accountEmailError, setAccountEmailError] = useState("");

  const [isPinWasEnabled, setIsPinWasEnabled] = useState<boolean>(false);
  const [isSettingUpPIN, setIsSettingUpPIN] = useState<boolean>(false);

  const isEmail: RegExp =
    // eslint-disable-next-line no-useless-escape
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  useEffect(() => {
    if (isPlatform("cordova")) {
      Keyboard.addListener("keyboardWillShow", () => {
        setIsOpenKeyboard(true);
      });
      Keyboard.addListener("keyboardDidHide", () => {
        setIsOpenKeyboard(false);
      });
    }
  }, []);

  useEffect(() => {
    if (!state.user.isFirstTimeLogin) {
      if (isPlatform("cordova")) {
        // mobile device, check if pin editor was closed and was setting up pin.
        if (
          !state.setApplicationPin && // pin modal wal closed
          isSettingUpPIN
        ) {
          if (state.authMode === AuthMode.PasscodeOnly) {
            // personalize default mode, updated state and vault
            setIsLoading(false);
            history.replace("/accounts");
            ShowToastSuccessMessage("Your first account successfully added");
          } else {
            setIsLoading(true);
          }
        }
      } else {
        // not mobile device, redirect to accounts
        history.replace("/accounts");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.user, state.setApplicationPin, state.authMode]);

  useEffect(() => {

    setIsPinWasEnabled(
      [AuthMode.PasscodeOnly, AuthMode.BiometricAndPasscode].includes(
        state.authMode
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.authMode]);

  const handleNext = async () => {
    let valid = true;

    const b = Buffer.from(accountLabel);
    if (!accountLabel) {
      valid = false;
      setAccountLabelError("Label is required.");
    } else if (!isValidUTF8(b)) {
      valid = false;
      setAccountLabelError("Invalid characters.");
    }

    if (!accountEmail) {
      valid = false;
      setAccountEmailError("Email address is required.");
    } else if (!isEmail.test(accountEmail)) {
      valid = false;
      setAccountEmailError("Please enter a valid email address.");
    }
    //  else if (accountEmail.length > 32) {
    //   valid = false;
    //   setAccountEmailError("Email must be less than 32 characters long.");
    // }

    // if (accountEmail.indexOf('_') !== -1) {
    //   valid = false;
    //   setAccountEmailError("Underscores are not allowed");
    // }

    if (valid) {
      setIsLoading(true);
      const identity = state.user.identity;

      const response: any = await updateUser(identity, {
        accountIcon: selectedIconName,
        accountColor: selectedColor,
        accountLabel: accountLabel,
        isFirstTimeLogin: false,
      });

      if (response?.status === 200) {
        const user: any = await getUserByIdentity(identity);

        if (user?.data?.id) {
          dispatch({ type: "setUser", user: user.data });
          dispatch({ type: "setAccount", account: user.data });
        }

        if (isPlatform("cordova") && !isPinWasEnabled) {
          slidesRef.current.slideNext();
        } else {
          history.replace("/accounts");
        }
      } else {
        const msg = response?.message || "Failed to personalize account.";
        ShowToastErrorMessage(msg, {
          autoClose: 4000,
        });
      }

      setIsLoading(false);
    } else {
      return;
    }
  };

  const handleEnablePIN = () => {
    localStorage.setItem("isOnlyPin", JSON.stringify(true));
    localStorage.setItem("isPersonalizePIN", "y"); // @TODISCUSS
    dispatch({ type: "showApplicationsPIN", setApplicationsPin: true });
    setIsSettingUpPIN(true);
  };

  const handleAccountLabelChange = (e: any) => {
    const { value } = e.target;
    setAccountLabel(value);
  };

  const handleAccountEmailChange = (e: any) => {
    const { value } = e.target;
    setAccountEmail(value);
  };

  return (
    <IonPage className="personalize-page">
      <IonContent>
        <IonSlides
          pager={true}
          options={slideOpts}
          mode="ios"
          ref={slidesRef}
          className={clsx("swiper-no-swiping", {
            "on-keyboard": isOpenKeyboard,
          })}
        >
          <IonSlide>
            <div className="slide-content-container">
              <IonIcon icon={abaxxLogo} className="app-logo" />
              <p className="subtitle">Personalize your account</p>

              <div className="personalize-account-form">
                <IonList lines="none" mode="ios">
                  <IonItem detail={false}>
                    <IonLabel position="stacked">Label</IonLabel>
                    <IonInput
                      name="accountLabel"
                      placeholder="Enter label"
                      className={clsx({ "has-error": !!accountLabelError })}
                      value={accountLabel}
                      type="text"
                      onIonChange={handleAccountLabelChange}
                      maxlength={48}
                    />
                    <div
                      className="label-icon-btn"
                      id="trigger-button"
                      style={{
                        background: selectedColor,
                      }}
                    >
                      <IonIcon icon={selectedIcon} />
                    </div>
                    {accountLabelError && (
                      <div className="error-text">{accountLabelError}</div>
                    )}
                  </IonItem>

                  <IonItem detail={false}>
                    <IonLabel position="stacked">Email Address</IonLabel>
                    <IonInput
                      disabled
                      name="accountEmail"
                      type="email"
                      inputMode="email"
                      placeholder="Enter your email"
                      className={clsx({ "has-error": !!accountEmailError })}
                      value={accountEmail}
                      maxlength={144}
                      onIonChange={handleAccountEmailChange}
                    />
                    {accountEmailError && (
                      <div className="error-text">{accountEmailError}</div>
                    )}
                  </IonItem>
                </IonList>

                <IonButton
                  color={!accountLabel || !accountEmail ? "light" : "danger"}
                  expand="full"
                  shape="round"
                  className={clsx("next-btn", {
                    disabled: !accountLabel || !accountEmail,
                  })}
                  disabled={!accountLabel || !accountEmail}
                  onClick={handleNext}
                >
                  Next
                </IonButton>
              </div>
            </div>
          </IonSlide>

          {isPlatform("cordova") && !isPinWasEnabled && (
            <IonSlide>
              <div className="slide-content-container">
                <IonIcon icon={abaxxLogo} className="app-logo" />
                <p className="subtitle">Pin Lock</p>

                <div className="pin-lock-container">
                  <IonImg src={phoneLockImage} />

                  <p>
                    Enable PIN from your device to unlock the
                    application and confirm when switching between accounts
                  </p>

                  <IonButton
                    color="danger"
                    expand="full"
                    shape="round"
                    onClick={handleEnablePIN}
                  >
                    Enable PIN
                  </IonButton>
                </div>
              </div>
            </IonSlide>
          )}
        </IonSlides>

        <LabelIconDropdown
          selectedIcon={selectedIcon}
          selectedIconName={selectedIconName}
          selectedColor={selectedColor}
          onSetSelectedIcon={setSelectedIcon}
          onSetSelectedIconName={setSelectedIconName}
          onSetSelectedColor={setSelectedColor}
        />

        <IonLoading
          isOpen={isLoading}
          onDidDismiss={() => setIsLoading(false)}
        />
      </IonContent>
    </IonPage>
  );
};

export default Personalize;
